// @flow

import { startRequest, finishRequest } from "@store/utils";
import {
  GetReportingMiReportsMyPresentations,
  GetReportingMiReportsMyPresentationsSuccess,
  GetReportingMiReportsMyPresentationsError,
  GetReportingMiReportsSharedPresentations,
  GetReportingMiReportsSharedPresentationsSuccess,
  GetReportingMiReportsSharedPresentationsError,
  GetReportingMiReportsMiSlides,
  GetReportingMiReportsMiSlidesSuccess,
  GetReportingMiReportsMiSlidesError,
  ReportingMiReportsSelectSlide,
  MiReportsCreateNewPresentation,
  MiReportsCreateNewPresentationSuccess,
  MiReportsCreateNewPresentationError,
  MiReportsUpdatePresentation,
  MiReportsUpdatePresentationSuccess,
  MiReportsUpdatePresentationError,
  GetReportingMiReportsPresentationLogs,
  GetReportingMiReportsPresentationLogsSuccess,
  GetReportingMiReportsPresentationLogsError,
  MiReportsEmployeesToShareSearch,
  MiReportsEmployeesToShareSearchSuccess,
  MiReportsEmployeesToShareSearchError,
  MiReportsEmployeesToShareClear,
  DownloadMiReportsPresentation,
  DownloadMiReportsPresentationSuccess,
  DownloadMiReportsPresentationError,
} from "@modules/reporting/constants/actions";
import type { MiReportsState } from "./types";

export const initialState: MiReportsState = {
  requestsInProgress: 0,
  mySavedPresentations: {
    error: null,
    presentations: [],
  },
  sharedPresentations: {
    error: null,
    presentations: [],
  },
  miSlides: {
    error: null,
    slides: [],
  },
  miLogs: {
    error: null,
    logs: [],
  },
  shareEmployees: {
    error: null,
    list: [],
  },
};

const setLoading = (state: MiReportsState) => () => startRequest(state);

const setLoaded = (state: MiReportsState) => () => finishRequest(state);

export const onGetReportingMiReportsMyPresentationsSuccess = (state: MiReportsState) => ({
  payload,
}: Object) => ({
  ...finishRequest(state),
  mySavedPresentations: {
    error: null,
    presentations: payload.content,
  },
});

export const onGetReportingMiReportsMyPresentationsError = (state: MiReportsState) => ({
  payload,
}: Object) => ({
  ...finishRequest(state),
  mySavedPresentations: {
    ...state.mySavedPresentations,
    error: payload,
  },
});

export const onGetReportingMiReportsSharedPresentationsSuccess = (state: MiReportsState) => ({
  payload,
}: Object) => ({
  ...finishRequest(state),
  sharedPresentations: {
    error: null,
    presentations: payload.content,
  },
});

export const onGetReportingMiReportsSharedPresentationsError = (state: MiReportsState) => ({
  payload,
}: Object) => ({
  ...finishRequest(state),
  sharedPresentations: {
    ...state.sharedPresentations,
    error: payload,
  },
});

export const onGetReportingMiReportsMiSlides = (state: MiReportsState) => ({
  payload,
}: Object) => ({
  ...startRequest(state),
  miSlides: {
    error: null,
    presentations: [],
  },
});

export const onGetReportingMiReportsMiSlidesSuccess = (state: MiReportsState) => ({
  payload,
}: Object) => ({
  ...finishRequest(state),
  miSlides: {
    error: null,
    slides: !payload.selectedSlides
      ? payload.data
      : payload.data.map(item =>
          payload.selectedSlides.includes(item.id) ? { ...item, selected: true } : item,
        ),
  },
});

export const onGetReportingMiReportsMiSlidesError = (state: MiReportsState) => ({
  payload,
}: Object) => ({
  ...finishRequest(state),
  miSlides: {
    ...state.miSlides,
    error: payload,
  },
});

export const onReportingMiReportsSelectSlide = (state: MiReportsState) => ({
  payload,
}: Object) => ({
  ...state,
  miSlides: {
    ...state.miSlides,
    slides: state.miSlides.slides.map(slide =>
      slide.id === payload ? { ...slide, selected: !slide.selected } : slide,
    ),
  },
});

export const onMiReportsCreateNewPresentationError = (state: MiReportsState) => ({
  payload,
}: Object) => ({
  ...finishRequest(state),
  mySavedPresentations: {
    ...state.mySavedPresentations,
    error: payload,
  },
});

export const onMiReportsUpdatePresentationError = (state: MiReportsState) => ({
  payload,
}: Object) => ({
  ...finishRequest(state),
  mySavedPresentations: {
    ...state.mySavedPresentations,
    error: payload,
  },
});

export const onGetReportingMiReportsPresentationLogs = (state: MiReportsState) => ({
  payload,
}: Object) => ({
  ...startRequest(state),
  miLogs: {
    error: null,
    logs: [],
  },
});

export const onGetReportingMiReportsPresentationLogsSuccess = (state: MiReportsState) => ({
  payload,
}: Object) => ({
  ...finishRequest(state),
  miLogs: {
    error: null,
    logs: payload,
  },
});

export const onGetReportingMiReportsPresentationLogsError = (state: MiReportsState) => ({
  payload,
}: Object) => ({
  ...finishRequest(state),
  miLogs: {
    ...state.miLogs,
    error: payload,
  },
});

export const onMiReportsEmployeesToShareSearchSuccess = (state: MiReportsState) => ({
  payload,
}: Object) => ({
  ...finishRequest(state),
  shareEmployees: {
    error: null,
    list: payload.content,
  },
});

export const onMiReportsEmployeesToShareSearchError = (state: MiReportsState) => ({
  payload,
}: Object) => ({
  ...finishRequest(state),
  shareEmployees: {
    ...state.shareEmployees,
    error: payload,
  },
});

export const onMiReportsEmployeesToShareClear = (state: MiReportsState) => ({
  payload,
}: Object) => ({
  ...state,
  shareEmployees: {
    ...state.shareEmployees,
    list: [],
  },
});

export default (state: MiReportsState = initialState, action: Object): Object => {
  const handlers = {
    [GetReportingMiReportsMyPresentations]: setLoading,
    [GetReportingMiReportsMyPresentationsSuccess]: onGetReportingMiReportsMyPresentationsSuccess,
    [GetReportingMiReportsMyPresentationsError]: onGetReportingMiReportsMyPresentationsError,
    [GetReportingMiReportsSharedPresentations]: setLoading,
    [GetReportingMiReportsSharedPresentationsSuccess]: onGetReportingMiReportsSharedPresentationsSuccess,
    [GetReportingMiReportsSharedPresentationsError]: onGetReportingMiReportsSharedPresentationsError,
    [GetReportingMiReportsMiSlides]: onGetReportingMiReportsMiSlides,
    [GetReportingMiReportsMiSlidesSuccess]: onGetReportingMiReportsMiSlidesSuccess,
    [GetReportingMiReportsMiSlidesError]: onGetReportingMiReportsMiSlidesError,
    [ReportingMiReportsSelectSlide]: onReportingMiReportsSelectSlide,
    [MiReportsCreateNewPresentation]: setLoading,
    [MiReportsCreateNewPresentationSuccess]: setLoaded,
    [MiReportsCreateNewPresentationError]: onMiReportsCreateNewPresentationError,
    [MiReportsUpdatePresentation]: setLoading,
    [MiReportsUpdatePresentationSuccess]: setLoaded,
    [MiReportsUpdatePresentationError]: onMiReportsUpdatePresentationError,
    [GetReportingMiReportsPresentationLogs]: onGetReportingMiReportsPresentationLogs,
    [GetReportingMiReportsPresentationLogsSuccess]: onGetReportingMiReportsPresentationLogsSuccess,
    [GetReportingMiReportsPresentationLogsError]: onGetReportingMiReportsPresentationLogsError,
    [MiReportsEmployeesToShareSearch]: setLoading,
    [MiReportsEmployeesToShareSearchSuccess]: onMiReportsEmployeesToShareSearchSuccess,
    [MiReportsEmployeesToShareSearchError]: onMiReportsEmployeesToShareSearchError,
    [MiReportsEmployeesToShareClear]: onMiReportsEmployeesToShareClear,
    [DownloadMiReportsPresentation]: setLoading,
    [DownloadMiReportsPresentationSuccess]: setLoaded,
    [DownloadMiReportsPresentationError]: setLoaded,
  };

  if (handlers[action.type]) {
    return handlers[action.type](state)(action);
  }
  return state;
};
